import { Container, Graphics, type Sprite } from 'pixi.js';
import type { PaletteToken } from '../../shared/palette';
import type { EntityId, SimEvent, SimState } from '../../sim/state';
import { BURST_AT } from '../boss-moments';
import type { Family, RenderContext } from '../context';
import type { EnemyPose } from '../enemies';
import { MASK_BODY, maskTone } from '../face-kit';
import type { Frame } from '../frame';
import { add, setTint } from '../util';
import { ViewPool } from '../views';
import { EXIT_BEATS, RAIN, SHREDS, rainDrop, rainbowAlpha, rayAlpha, shred } from './orage-moments';
import { createPuddles } from './orage-puddles';

const KIND = 'orage';
const SHADOW = { x: 0.3, y: 0.85, width: 1.8, height: 0.85, alpha: 0.45 };
const DROP = { width: 1.3, dx: -2, dy: 7 };
const RAYS = { count: 6, reach: 6, spread: 0.28, half: 0.05, lift: 0.25 };
const RAINBOW = {
  tokens: ['mage', 'tank', 'noyau', 'healer', 'turquoise', 'or'] satisfies PaletteToken[],
  from: 2.3,
  step: 0.125,
  width: 0.125,
  y: 0.75,
  start: Math.PI * 1.08,
  end: Math.PI * 1.92,
};
const SHRED = { big: 0.2, small: 0.125, shift: 0.175, rim: 2 };

interface Boss {
  readonly shadow: Sprite;
}

interface Exit {
  readonly start: number;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
}

export function createOrage(
  ctx: RenderContext,
  poseOf: (id: EntityId) => EnemyPose | undefined,
): Family {
  const { layers, textures } = ctx;
  const puddles = createPuddles(ctx);
  const shadows = layers.dim.addChild(new Container());
  const rain = layers.enemyShots.addChild(new Graphics());
  const sky = layers.fx.addChild(new Graphics());
  const exits: Exit[] = [];
  const bosses = new ViewPool<Boss>(
    () => ({ shadow: add(shadows, textures.halo) }),
    (boss) => {
      boss.shadow.visible = false;
    },
  );

  function hover(boss: Boss, pose: EnemyPose): void {
    const { shadow } = boss;
    const { radius } = pose;
    shadow.visible = true;
    setTint(shadow, 0x000000);
    shadow.position.set(pose.x + SHADOW.x * radius, pose.y + SHADOW.y * radius);
    shadow.scale.set(
      (SHADOW.width * radius) / textures.halo.radius,
      (SHADOW.height * radius) / textures.halo.radius,
    );
    shadow.alpha = SHADOW.alpha * pose.sprite.alpha;
  }

  function shower(pose: EnemyPose, beats: number, frame: Frame): void {
    const { x, y, radius } = pose;
    for (let index = 0; index < RAIN.drops; index += 1) {
      const drop = rainDrop(index, beats, frame.calm);
      const dx = x + drop.x * radius;
      const dy = y + drop.y * radius;
      rain
        .moveTo(dx, dy)
        .lineTo(dx + DROP.dx, dy + DROP.dy)
        .stroke({
          width: DROP.width,
          color: frame.palette.badVibeRim,
          alpha: drop.alpha * pose.sprite.alpha,
          cap: 'round',
        });
    }
  }

  function leave(exit: Exit, frame: Frame): boolean {
    const p = ((frame.show - exit.start) / frame.tempo.ticksPerBeat - BURST_AT) / EXIT_BEATS;
    if (p >= 1) {
      return false;
    }
    if (p < 0) {
      return true;
    }
    const { x, y, radius } = exit;
    const { palette } = frame;
    const heart = y - RAYS.lift * radius;
    for (let index = 0; index < RAYS.count; index += 1) {
      const angle =
        -Math.PI / 2 + (index - (RAYS.count - 1) / 2) * RAYS.spread + Math.sin(p * 2) * 0.05;
      sky
        .moveTo(x, heart)
        .arc(x, heart, RAYS.reach * radius, angle - RAYS.half, angle + RAYS.half)
        .closePath()
        .fill({ color: palette.noyau, alpha: rayAlpha(p) });
    }
    RAINBOW.tokens.forEach((token, index) => {
      const bow = (RAINBOW.from - index * RAINBOW.step) * radius;
      const by = y + RAINBOW.y * radius;
      sky
        .moveTo(x + Math.cos(RAINBOW.start) * bow, by + Math.sin(RAINBOW.start) * bow)
        .arc(x, by, bow, RAINBOW.start, RAINBOW.end)
        .stroke({
          width: RAINBOW.width * radius,
          color: palette[token],
          alpha: rainbowAlpha(p),
        });
    });
    const rim = palette.badVibeRim;
    const body = maskTone(rim, MASK_BODY);
    for (let index = 0; index < SHREDS; index += 1) {
      const bit = shred(index, p);
      const bx = x + bit.x * radius;
      const by = y + bit.y * radius;
      sky
        .circle(bx, by, SHRED.big * radius * bit.scale)
        .circle(
          bx + SHRED.shift * radius * bit.scale,
          by + 0.05 * radius,
          SHRED.small * radius * bit.scale,
        )
        .fill({ color: body, alpha: bit.alpha })
        .stroke({ width: SHRED.rim, color: rim, alpha: bit.alpha });
    }
    return true;
  }

  return {
    update(state: SimState, alpha: number, frame: Frame): void {
      puddles.update(state, alpha, frame);
      rain.clear();
      sky.clear();
      bosses.begin();
      const beats = frame.now / frame.tempo.ticksPerBeat;
      for (const enemy of state.enemies) {
        const pose = enemy.kind === KIND ? poseOf(enemy.id) : undefined;
        if (pose === undefined) {
          continue;
        }
        hover(bosses.acquire(enemy.id), pose);
        shower(pose, beats, frame);
      }
      bosses.end();
      for (let index = exits.length - 1; index >= 0; index -= 1) {
        const exit = exits[index];
        if (exit !== undefined && !leave(exit, frame)) {
          exits.splice(index, 1);
        }
      }
    },
    onEvent(event: SimEvent, _state: SimState, frame: Frame): void {
      if (event.type === 'enemyDied' && event.kind === KIND) {
        const radius = poseOf(event.id)?.radius ?? ctx.enemyRadii?.get(KIND) ?? 40;
        exits.push({ start: frame.show, x: event.x, y: event.y, radius });
      }
    },
    reset(): void {
      puddles.reset();
      bosses.releaseAll();
      exits.length = 0;
      rain.clear();
      sky.clear();
    },
    destroy(): void {
      puddles.destroy();
      for (const container of [shadows, rain, sky]) {
        container.destroy({ children: true });
      }
    },
  };
}
